import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { getPropertyBySlug, createRequest } from '../../lib/api';
import { 
  Home, 
  MapPin, 
  Book, 
  Compass, 
  MessageSquare, 
  Heart,
  Wifi,
  Car,
  Clock,
  Phone,
  ChevronRight,
  Send,
  Check,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export const GuestHub = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [activeTab, setActiveTab] = useState('home');

  useEffect(() => {
    loadProperty();
  }, [slug]);

  const loadProperty = async () => {
    try {
      const res = await getPropertyBySlug(slug);
      setProperty(res.data);
    } catch (error) {
      console.error('Failed to load property:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] p-6">
        <div className="max-w-2xl mx-auto space-y-6">
          <Skeleton className="h-48 w-full rounded-2xl" />
          <Skeleton className="h-12 w-3/4 mx-auto" />
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading text-stone-900 mb-2">Property Not Found</h1>
          <p className="text-stone-500">The property you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const navItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'checkin', label: 'Check-in', icon: MapPin },
    { id: 'guide', label: 'Guide', icon: Book },
    { id: 'recommendations', label: 'Local', icon: Compass },
    { id: 'requests', label: 'Request', icon: MessageSquare },
    { id: 'feedback', label: 'Feedback', icon: Heart },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="guesthub-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-xl text-[#4A5D4F]">{property.name}</h1>
              <p className="text-sm text-stone-500">{property.location}</p>
            </div>
            <Badge variant="outline" className="text-xs bg-white/80 hover:bg-white cursor-pointer">Need help?</Badge>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="max-w-4xl mx-auto px-4 pb-2 overflow-x-auto">
          <nav className="flex gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeTab === item.id
                      ? 'bg-[#4A5D4F] text-white'
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {activeTab === 'home' && <HomeTab property={property} setActiveTab={setActiveTab} />}
        {activeTab === 'checkin' && <CheckinTab property={property} />}
        {activeTab === 'guide' && <GuideTab property={property} />}
        {activeTab === 'recommendations' && <RecommendationsTab property={property} />}
        {activeTab === 'requests' && <RequestsTab property={property} />}
        {activeTab === 'feedback' && <FeedbackTab property={property} />}
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-200 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-stone-400">Powered by Maris Desk</p>
        </div>
      </footer>
    </div>
  );
};

const HomeTab = ({ property, setActiveTab }) => (
  <div className="space-y-8 animate-fade-in">
    <div className="text-center py-8">
      <h2 className="text-3xl font-heading text-stone-900 mb-4">Welcome</h2>
      <p className="text-lg text-stone-600 max-w-xl mx-auto">
        {property.guestHubContent?.welcome || `Welcome to ${property.name}. We're delighted to have you.`}
      </p>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {[
        { id: 'checkin', label: 'Check-in Info', icon: MapPin, color: 'bg-blue-50 text-blue-600' },
        { id: 'guide', label: 'House Guide', icon: Book, color: 'bg-green-50 text-green-600' },
        { id: 'recommendations', label: 'Local Tips', icon: Compass, color: 'bg-amber-50 text-amber-600' },
        { id: 'requests', label: 'Request Something', icon: MessageSquare, color: 'bg-purple-50 text-purple-600' },
        { id: 'feedback', label: 'Share Feedback', icon: Heart, color: 'bg-red-50 text-red-600' },
      ].map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className="p-6 bg-white rounded-2xl border border-stone-200 card-hover text-left transition-all"
          >
            <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mb-4`}>
              <Icon className="h-6 w-6" />
            </div>
            <p className="font-medium text-stone-900">{item.label}</p>
            <ChevronRight className="h-4 w-4 text-stone-400 mt-2" />
          </button>
        );
      })}
    </div>

    {/* Contact */}
    <Card className="border-stone-200">
      <CardContent className="p-6">
        <h3 className="font-heading text-lg mb-4">Need Help?</h3>
        <div className="space-y-3">
          {property.contactPhone && (
            <a href={`tel:${property.contactPhone}`} className="flex items-center gap-3 text-stone-600 hover:text-[#4A5D4F]">
              <Phone className="h-5 w-5" />
              {property.contactPhone}
            </a>
          )}
          {property.contactEmail && (
            <a href={`mailto:${property.contactEmail}`} className="flex items-center gap-3 text-stone-600 hover:text-[#4A5D4F]">
              <MessageSquare className="h-5 w-5" />
              {property.contactEmail}
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  </div>
);

const CheckinTab = ({ property }) => (
  <div className="space-y-6 animate-fade-in">
    <h2 className="text-2xl font-heading text-stone-900">Check-in Information</h2>
    
    <Card className="border-stone-200">
      <CardContent className="p-6 space-y-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#4A5D4F]/10 flex items-center justify-center">
            <MapPin className="h-6 w-6 text-[#4A5D4F]" />
          </div>
          <div>
            <h3 className="font-medium text-stone-900">Location & Access</h3>
            <p className="text-stone-600 mt-1">
              {property.guestHubContent?.checkin || 'Check-in details will be provided by your host.'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
            <Clock className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-stone-900">Check-in & Check-out Times</h3>
            <p className="text-stone-600 mt-1">
              Check-in: {property.policies?.checkIn || '3:00 PM'}<br />
              Check-out: {property.policies?.checkOut || '11:00 AM'}
            </p>
          </div>
        </div>

        {property.escalationContacts?.maintenance && (
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
              <Phone className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-medium text-stone-900">Emergency Contact</h3>
              <p className="text-stone-600 mt-1">{property.escalationContacts.maintenance}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

const GuideTab = ({ property }) => (
  <div className="space-y-6 animate-fade-in">
    <h2 className="text-2xl font-heading text-stone-900">House Guide</h2>
    
    <Card className="border-stone-200">
      <CardContent className="p-6">
        <p className="text-stone-600 whitespace-pre-wrap">
          {property.guestHubContent?.guide || 'House guide information will be provided by your host.'}
        </p>
      </CardContent>
    </Card>

    {/* FAQs */}
    {property.faqItems && property.faqItems.length > 0 && (
      <div className="space-y-4">
        <h3 className="font-heading text-lg text-stone-900">Frequently Asked</h3>
        {property.faqItems.map((faq, idx) => (
          <Card key={idx} className="border-stone-200">
            <CardContent className="p-4">
              <p className="font-medium text-stone-900">{faq.q}</p>
              <p className="text-stone-600 mt-1">{faq.a}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    )}
  </div>
);

const RecommendationsTab = ({ property }) => (
  <div className="space-y-6 animate-fade-in">
    <h2 className="text-2xl font-heading text-stone-900">Local Recommendations</h2>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {property.guestHubContent?.recommendations?.map((rec, idx) => (
        <Card key={idx} className="border-stone-200 card-hover">
          <CardContent className="p-4">
            <Badge variant="secondary" className="text-xs mb-2">{rec.category}</Badge>
            <p className="font-medium text-stone-900">{rec.name}</p>
            <p className="text-sm text-stone-600 mt-1">{rec.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>

    {(!property.guestHubContent?.recommendations || property.guestHubContent.recommendations.length === 0) && (
      <Card className="border-stone-200">
        <CardContent className="p-12 text-center">
          <Compass className="h-12 w-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">Local recommendations coming soon</p>
        </CardContent>
      </Card>
    )}
  </div>
);

const RequestsTab = ({ property }) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    guestName: '',
    guestContact: '',
    requestType: 'OTHER',
    details: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.guestName || !formData.guestContact || !formData.details) {
      toast.error('Please fill all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await createRequest({
        propertyId: property.id,
        ...formData
      });
      setSubmitted(true);
      toast.success('Request submitted!');
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="border-stone-200">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="font-heading text-xl text-stone-900 mb-2">Request Received!</h3>
            <p className="text-stone-600">We'll get back to you shortly.</p>
            <Button 
              variant="outline" 
              className="mt-6 rounded-full"
              onClick={() => {
                setSubmitted(false);
                setFormData({ guestName: '', guestContact: '', requestType: 'OTHER', details: '' });
              }}
            >
              Submit Another Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-2xl font-heading text-stone-900">Request Something</h2>
      
      <Card className="border-stone-200">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Your Name *</Label>
              <Input
                value={formData.guestName}
                onChange={(e) => setFormData({...formData, guestName: e.target.value})}
                placeholder="Full name"
                data-testid="request-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact (Email or Phone) *</Label>
              <Input
                value={formData.guestContact}
                onChange={(e) => setFormData({...formData, guestContact: e.target.value})}
                placeholder="email@example.com or +1 234 567 8900"
                data-testid="request-contact-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Request Type</Label>
              <Select value={formData.requestType} onValueChange={(v) => setFormData({...formData, requestType: v})}>
                <SelectTrigger data-testid="request-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TRANSFER">Airport Transfer</SelectItem>
                  <SelectItem value="LATE_CHECKOUT">Late Checkout</SelectItem>
                  <SelectItem value="HOUSEKEEPING">Housekeeping</SelectItem>
                  <SelectItem value="CHEF">Private Chef</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Details *</Label>
              <Textarea
                value={formData.details}
                onChange={(e) => setFormData({...formData, details: e.target.value})}
                placeholder="Tell us what you need..."
                rows={4}
                data-testid="request-details-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-[#4A5D4F] hover:bg-[#3d4d42] rounded-full"
              disabled={submitting}
              data-testid="submit-request-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Submit Request
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Upsells */}
      {property.upsells && property.upsells.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-heading text-lg text-stone-900">Popular Add-ons</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {property.upsells.map((upsell, idx) => (
              <Card key={idx} className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-stone-900">{upsell.name}</p>
                      <p className="text-sm text-stone-600 mt-1">{upsell.description}</p>
                    </div>
                    <Badge variant="secondary">{upsell.price}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const FeedbackTab = ({ property }) => {
  const [submitted, setSubmitted] = useState(false);
  const [feedback, setFeedback] = useState({
    experience: '',
    cleanliness: '',
    communication: '',
    comments: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    toast.success('Thank you for your feedback!');
  };

  if (submitted) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="border-stone-200">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Heart className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="font-heading text-xl text-stone-900 mb-2">Thank You!</h3>
            <p className="text-stone-600">Your feedback helps us improve.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const RatingSelect = ({ label, value, onChange }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        {['😞', '😐', '😊', '😄', '🤩'].map((emoji, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => onChange(idx + 1)}
            className={`w-12 h-12 text-2xl rounded-xl border transition-all ${
              value === idx + 1 
                ? 'border-[#4A5D4F] bg-[#4A5D4F]/10 scale-110' 
                : 'border-stone-200 hover:border-stone-300'
            }`}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-2xl font-heading text-stone-900">Share Your Feedback</h2>
      
      <Card className="border-stone-200">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <RatingSelect 
              label="Overall Experience" 
              value={feedback.experience}
              onChange={(v) => setFeedback({...feedback, experience: v})}
            />
            <RatingSelect 
              label="Cleanliness" 
              value={feedback.cleanliness}
              onChange={(v) => setFeedback({...feedback, cleanliness: v})}
            />
            <RatingSelect 
              label="Communication" 
              value={feedback.communication}
              onChange={(v) => setFeedback({...feedback, communication: v})}
            />
            <div className="space-y-2">
              <Label>Additional Comments</Label>
              <Textarea
                value={feedback.comments}
                onChange={(e) => setFeedback({...feedback, comments: e.target.value})}
                placeholder="Share any thoughts or suggestions..."
                rows={4}
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-[#4A5D4F] hover:bg-[#3d4d42] rounded-full"
            >
              <Heart className="h-4 w-4 mr-2" />
              Submit Feedback
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default GuestHub;
